import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import { GraduationCap, ChevronDown, ChevronUp, ArrowUp, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import AcademyComments from "@/components/AcademyComments";
import Seo from "@/components/Seo";

export default function Academy() {
  const { t, lang, isRTL } = useI18n();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { data, isLoading } = useQuery({
    queryKey: ["academy"],
    queryFn: () => api.get("/academy").then((r) => r.data),
  });
  const [trackIdx, setTrackIdx] = useState(0);
  const [chapterIdx, setChapterIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const contentRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link: ?t=<track_id>&c=<chapter_id> picks the right track + chapter.
  useEffect(() => {
    if (!data) return;
    const tId = searchParams.get("t");
    const cId = searchParams.get("c");
    if (!cId) return;
    const tracks = data.tracks || [{ id: "kasani", ...data }];
    let ti = tId ? tracks.findIndex((x) => x.id === tId) : -1;
    let ci = -1;
    if (ti >= 0) {
      ci = (tracks[ti].chapters || []).findIndex((c) => c.id === cId);
    } else {
      // fallback: search all tracks for the chapter id
      for (let i = 0; i < tracks.length; i += 1) {
        const j = (tracks[i].chapters || []).findIndex((c) => c.id === cId);
        if (j >= 0) { ti = i; ci = j; break; }
      }
    }
    if (ti >= 0 && ci >= 0) {
      setTrackIdx(ti);
      setChapterIdx(ci);
      setPickerOpen(false);
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("t");
    next.delete("c");
    // Preserve hash (e.g. #comment-{id}) which setSearchParams would otherwise drop.
    const currentHash = (typeof window !== "undefined" && window.location.hash) || "";
    setSearchParams(next, { replace: true });
    if (currentHash && typeof window !== "undefined" && !window.location.hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${next.toString() ? `?${next.toString()}` : ""}${currentHash}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Fetch user's completed chapters (only if logged in)
  const { data: progressData } = useQuery({
    queryKey: ["progress-academy", user?.user_id],
    queryFn: () => endpoints.listProgress("academy"),
    enabled: !!user,
  });
  const completedSet = useMemo(
    () => new Set(progressData?.completed || []),
    [progressData],
  );

  // Compute active chapter id (safe even before data loads) for the auto-mark effect
  const activeChapterId = useMemo(() => {
    if (!data) return null;
    const t = (data.tracks || [{ id: "kasani", ...data }])[trackIdx];
    const chs = t?.chapters || [];
    return chs[chapterIdx]?.id || null;
  }, [data, trackIdx, chapterIdx]);

  // Auto-mark the current chapter as completed after 8s dwell.
  useEffect(() => {
    if (!user || !activeChapterId) return;
    if (completedSet.has(activeChapterId)) return;
    const timer = setTimeout(async () => {
      try {
        const res = await endpoints.markProgressComplete("academy", activeChapterId);
        queryClient.invalidateQueries({ queryKey: ["progress-academy", user.user_id] });
        queryClient.invalidateQueries({ queryKey: ["progress-summary", user.user_id] });
        if (Array.isArray(res?.newly_unlocked)) {
          res.newly_unlocked.forEach((a) => {
            const title = isRTL ? a.title_ar : a.title_en;
            toast.success(isRTL ? `🏆 إنجاز جديد: ${title}` : `🏆 New achievement: ${title}`);
          });
        }
      } catch (_) { /* silent */ }
    }, 8000);
    return () => clearTimeout(timer);
  }, [activeChapterId, user, completedSet, queryClient, isRTL]);

  if (isLoading || !data) {
    return <div className="max-w-4xl mx-auto p-20 text-[#5C544F]" data-testid="academy-loading">{t.common.loading}</div>;
  }

  const tracks = data.tracks || [{ id: "kasani", ...data }];
  const track = tracks[trackIdx] || tracks[0];
  const instructor = track.instructor;
  const chapters = track.chapters || [];
  const ch = chapters[chapterIdx] || chapters[0];

  const handleSelectTrack = (idx) => {
    setTrackIdx(idx);
    setChapterIdx(0);
    setPickerOpen(false);
  };

  const selectChapter = (i) => {
    setChapterIdx(i);
    setPickerOpen(false);
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const scrollToPicker = () => {
    pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPickerOpen(true);
  };

  const goPrev = () => chapterIdx > 0 && selectChapter(chapterIdx - 1);
  const goNext = () => chapterIdx < chapters.length - 1 && selectChapter(chapterIdx + 1);
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 lg:pt-12 pb-24" data-testid="academy-page">
      <Seo
        title={isRTL ? "أكاديمية الباريستا — دورة شاملة بمعايير SCA" : "Barista Academy — SCA-aligned full course"}
        description={isRTL
          ? "دورة باريستا أونلاين شاملة باللغة العربية — الإسبريسو، اللاتيه آرت، تحضير الحليب، طحن البن، التذوق وعجلة النكهات وفق معايير SCA. أفضل دورة باريستا أونلاين بالعربية."
          : "Comprehensive online barista course in Arabic — espresso pulling, milk steaming, latte art, grinding, cupping & SCA flavor wheel. SCA-aligned."}
        keywords="دورة باريستا أونلاين, أفضل دورة باريستا أونلاين, دورات باريستا, دورة باريستا بالعربية, دورة علوم القهوة باللغة العربية, شهادة SCA, SCA Coffee Courses, شهادة باريستا, CQI"
        path="/academy"
        jsonLdId="academy-jsonld"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": isRTL ? "أكاديمية الباريستا — دورة شاملة بمعايير SCA" : "Barista Academy — SCA-aligned full course",
          "description": isRTL ? "دورة كاملة في مهارات الباريستا الاحترافي بمعايير SCA باللغة العربية." : "Full barista skills course aligned with SCA standards.",
          "provider": { "@type": "EducationalOrganization", "name": "Abdullah Coffee Science Academy", "sameAs": "https://abdullah-coffee-science.academy/" },
          "url": "https://abdullah-coffee-science.academy/academy",
          "inLanguage": ["ar", "en"],
          "educationalLevel": "Beginner to Advanced",
          "about": ["Barista Skills", "Espresso", "Latte Art", "SCA Standards", "Specialty Coffee"],
          "hasCourseInstance": { "@type": "CourseInstance", "courseMode": "online", "courseWorkload": "PT20H" },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "SAR", "availability": "https://schema.org/InStock" }
        }}
      />
      {/* Track switcher */}
      {tracks.length > 1 && (
        <div
          className="inline-flex p-1 rounded-full bg-[#2A2421]/5 border border-[#2A2421]/10 mb-10"
          data-testid="academy-track-switcher"
        >
          {tracks.map((tr, idx) => {
            const active = idx === trackIdx;
            return (
              <button
                key={tr.id}
                onClick={() => handleSelectTrack(idx)}
                className={`px-5 py-2 text-xs uppercase tracking-[0.2em] rounded-full transition-colors ${active ? "bg-[#2A2421] text-[#F9F6F0]" : "text-[#5C544F] hover:text-[#2A2421]"}`}
                data-testid={`academy-track-${tr.id}`}
              >
                {lang === "ar" ? tr.instructor.title_ar : tr.instructor.title_en}
              </button>
            );
          })}
        </div>
      )}
      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-[#4A5D4E] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#4A5D4E]">{isRTL ? "أكاديمي" : "Academy"}</div>
          </div>
          <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>
            {lang === "ar" ? instructor.title_ar : instructor.title_en}
          </h1>
          <div className="mt-5 text-lg text-[#5C544F]">
            {isRTL ? "مع" : "with"}{" "}
            <span className="text-[#C86240] font-semibold">
              {lang === "ar" ? instructor.name_ar : instructor.name_en}
            </span>
          </div>
          <p className="mt-6 text-[#5C544F] leading-relaxed max-w-2xl">
            {track.id === "professional"
              ? (isRTL
                ? "مسار احترافي مبني على منهج CSP — معرفة أعمق في النبات، التحميص، الطحن، الإسبريسو، الحليب، والماء. اثنا عشر فصلاً يبني مهارة الباريستا الكاملة."
                : "A professional CSP-based track — deeper knowledge of botany, roasting, grinding, espresso, milk, and water. Twelve chapters that build a complete barista skillset.")
              : track.id === "beginner-barista"
              ? (isRTL
                ? "بداية الباريستا من الصفر: تجهيز المحطة، أول جرعة إسبريسو، تبخير الحليب، المشروبات الأساسية، والضيافة. ثمانية فصول قصيرة عملية تجهّزك للوقوف خلف البار."
                : "Barista from zero: setting up the station, your first espresso, steaming milk, the core drinks, and hospitality. Eight short, hands-on chapters that prepare you for the bar.")
              : (isRTL
                ? "محاور الدورة كاملة: من الفرق بين المختصة والتجارية، إلى تشريح كرزة البن، المعالجات، السلالات، التحميص، الطحن، الإسبريسو، وV60. عشر فصول تبني عينك ولسانك."
                : "The full course: from specialty vs commercial, through cherry anatomy, processing, cultivars, roasting, grinding, espresso, and V60. Ten chapters to train your eye and palate.")}
          </p>
        </div>
        <div className="lg:col-span-4 rounded-2xl p-6 bg-[#2A2421] text-[#F9F6F0] shadow-lg">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C86240] mb-3">{isRTL ? "محتوى الدورة" : "Course content"}</div>
          <div className={`${serifClass} text-5xl mb-2 text-white`}>{track.chapters.length}</div>
          <div className="text-sm text-[#F9F6F0]/80">{isRTL ? "فصول معرفية" : "knowledge chapters"}</div>
        </div>
      </div>

      {/* CHAPTER PICKER (collapsible) */}
      <div ref={pickerRef} className="mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[#4A5D4E] mb-3">
          {isRTL ? "اختر فصلاً" : "Pick a chapter"}
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-[#2A2421] text-white hover:bg-[#3A3128] transition-colors shadow-sm"
          data-testid="academy-picker-toggle"
          aria-expanded={pickerOpen}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className={`${serifClass} w-9 h-9 shrink-0 rounded-lg bg-[#4A5D4E] flex items-center justify-center text-white text-sm`}>
              {String(chapterIdx + 1).padStart(2, "0")}
            </span>
            <div className="text-left min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">
                {isRTL ? "الفصل الحالي" : "Current chapter"}
              </div>
              <div className={`${serifClass} text-base lg:text-lg leading-tight truncate`}>
                {lang === "ar" ? ch.title_ar : ch.title_en}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 shrink-0">
            <span className="hidden sm:inline">
              {pickerOpen ? (isRTL ? "إخفاء القائمة" : "Hide list") : (isRTL ? "اعرض كل الفصول" : "Show all chapters")}
            </span>
            {pickerOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {pickerOpen && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 p-4 rounded-2xl bg-white border border-[#2A2421]/10" data-testid="academy-picker-list">
            {chapters.map((c, i) => {
              const isActive = i === chapterIdx;
              const isDone = completedSet.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => selectChapter(i)}
                  data-testid={`academy-nav-${c.id}`}
                  className={`text-left flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                    isActive ? "bg-[#2A2421] text-[#F9F6F0]" : "text-[#2A2421] hover:bg-[#2A2421]/5 border border-transparent hover:border-[#4A5D4E]/20"
                  }`}
                >
                  <span className={`${serifClass} w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-sm ${
                    isActive ? "bg-[#C86240] text-white" : "bg-[#2A2421]/5 text-[#2A2421]"
                  }`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm flex-1 leading-tight">
                    {lang === "ar" ? c.title_ar : c.title_en}
                  </span>
                  {isDone && (
                    <CheckCircle2
                      className={`w-4 h-4 shrink-0 ${isActive ? "text-[#7FB287]" : "text-[#4A9966]"}`}
                      data-testid={`academy-done-${c.id}`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div ref={contentRef}>
        {/* Back to picker pill + progress */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button
            type="button"
            onClick={scrollToPicker}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-[#2A2421]/10 text-sm text-[#2A2421] hover:bg-[#4A5D4E] hover:text-white hover:border-[#4A5D4E] transition-all shadow-sm"
            data-testid="academy-back-to-picker"
          >
            <ArrowUp className="w-4 h-4" />
            <span>{isRTL ? "اختر فصلاً آخر" : "Pick another chapter"}</span>
          </button>
          <div className="flex items-center gap-3 min-w-[200px] sm:min-w-[280px]" data-testid="academy-progress">
            <span className="text-xs uppercase tracking-[0.25em] text-[#5C544F] whitespace-nowrap">
              {isRTL ? `${chapterIdx + 1} من ${chapters.length}` : `${chapterIdx + 1} of ${chapters.length}`}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-[#2A2421]/10 overflow-hidden">
              <div className="h-full bg-[#4A5D4E] transition-all" style={{ width: `${((chapterIdx + 1) / chapters.length) * 100}%` }} />
            </div>
          </div>
        </div>

        {/* Single chapter detail */}
        <article className="soft-card rounded-2xl overflow-hidden" data-testid={`academy-chapter-${ch.id}`}>
          <div className="p-7 flex items-start gap-6">
            <div className={`${serifClass} text-3xl text-[#C86240] opacity-70 leading-none`}>
              {String(chapterIdx + 1).padStart(2, "0")}
            </div>
            <div className="flex-1">
              <h2 className={`${serifClass} text-2xl lg:text-3xl text-[#2A2421] leading-tight`}>
                {lang === "ar" ? ch.title_ar : ch.title_en}
              </h2>
              <p className="mt-2 text-sm text-[#5C544F]">
                {lang === "ar" ? ch.subtitle_ar : ch.subtitle_en}
              </p>
            </div>
          </div>

          <div className="border-t border-[#2A2421]/10 p-7 lg:p-10 grid lg:grid-cols-12 gap-8" data-testid={`academy-chapter-content-${ch.id}`}>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-xl overflow-hidden">
                <img src={ch.image} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 space-y-6">
              {ch.body_en && (
                <p className="text-[#2A2421] leading-relaxed text-base">
                  {lang === "ar" ? ch.body_ar : ch.body_en}
                </p>
              )}

              {ch.layers && (
                <div className="space-y-3">
                  {ch.layers.map((l) => (
                    <div key={l.name_en} className="border-s-2 border-[#C86240]/40 ps-4">
                      <div className="text-sm font-semibold text-[#2A2421]">{lang === "ar" ? l.name_ar : l.name_en}</div>
                      <div className="text-sm text-[#5C544F] mt-0.5">{lang === "ar" ? l.desc_ar : l.desc_en}</div>
                    </div>
                  ))}
                </div>
              )}

              {ch.methods && (
                <div className="space-y-4">
                  {ch.methods.map((m) => (
                    <div key={m.name_en} className="rounded-xl bg-[#F9F6F0] p-4 border border-[#2A2421]/8">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className={`${serifClass} text-xl text-[#2A2421]`}>{lang === "ar" ? m.name_ar : m.name_en}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5D4E]">{lang === "ar" ? m.regions_ar : m.regions_en}</div>
                      </div>
                      <div className="mt-1 text-xs text-[#5C544F]">{lang === "ar" ? m.duration_ar : m.duration_en}</div>
                      <div className="mt-2 text-sm text-[#2A2421]">{lang === "ar" ? m.flavor_ar : m.flavor_en}</div>
                    </div>
                  ))}
                </div>
              )}

              {ch.species && (
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{isRTL ? "الأنواع" : "Species"}</div>
                  <div className="space-y-2 mb-6">
                    {ch.species.map((s) => (
                      <div key={s.name_en}>
                        <span className="font-semibold text-[#C86240]">{lang === "ar" ? s.name_ar : s.name_en}.</span>{" "}
                        <span className="text-[#2A2421]">{lang === "ar" ? s.desc_ar : s.desc_en}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{isRTL ? "السلالات" : "Cultivars"}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {ch.cultivars.map((c) => (
                      <div key={c.name} className="text-sm">
                        <span className="font-semibold text-[#2A2421]">{c.name}.</span>{" "}
                        <span className="text-[#5C544F]">{lang === "ar" ? c.desc_ar : c.desc_en}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ch.conditions && (
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {ch.conditions.map((c) => (
                    <div key={c.label_en}>
                      <dt className="text-[10px] uppercase tracking-[0.2em] text-[#5C544F]">{lang === "ar" ? c.label_ar : c.label_en}</dt>
                      <dd className="text-sm text-[#2A2421] mt-0.5">{c.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {ch.comparison && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#2A2421]/10">
                        <th className="text-start py-2 text-xs uppercase tracking-[0.2em] text-[#5C544F] font-normal"></th>
                        <th className="text-start py-2 text-xs uppercase tracking-[0.2em] text-[#5C544F] font-normal">{isRTL ? "فاتح" : "Light"}</th>
                        <th className="text-start py-2 text-xs uppercase tracking-[0.2em] text-[#5C544F] font-normal">{isRTL ? "غامق" : "Dark"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ch.comparison.map((row) => (
                        <tr key={row.label_en} className="border-b border-[#2A2421]/5">
                          <td className="py-2.5 text-[#5C544F] text-xs uppercase tracking-[0.15em]">{lang === "ar" ? row.label_ar : row.label_en}</td>
                          <td className="py-2.5 text-[#2A2421]">{row.light}</td>
                          <td className="py-2.5 text-[#2A2421]">{row.dark}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {ch.factors && (
                <div className="space-y-3">
                  {ch.factors.map((f) => (
                    <div key={f.label_en} className="border-s-2 border-[#4A5D4E]/40 ps-4">
                      <div className="text-sm font-semibold text-[#2A2421]">{lang === "ar" ? f.label_ar : f.label_en}</div>
                      <div className="text-sm text-[#5C544F]">{lang === "ar" ? f.desc_ar : f.desc_en}</div>
                    </div>
                  ))}
                </div>
              )}

              {ch.variables && (
                <>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {ch.variables.map((v) => (
                      <div key={v.label_en}>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-[#5C544F]">{lang === "ar" ? v.label_ar : v.label_en}</dt>
                        <dd className="text-sm text-[#2A2421] mt-0.5">{v.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{isRTL ? "حسب درجة التحميص" : "By roast level"}</div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {ch.roast_profiles.map((p) => (
                        <div key={p.roast_en} className="rounded-xl bg-[#F9F6F0] p-4 border border-[#2A2421]/8">
                          <div className={`${serifClass} text-lg text-[#C86240]`}>{lang === "ar" ? p.roast_ar : p.roast_en}</div>
                          <div className="text-sm text-[#2A2421] mt-1">{p.ratio}</div>
                          <div className="text-xs text-[#5C544F]">{p.example}</div>
                          <div className="text-xs text-[#5C544F]">{p.temp}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {ch.params && (
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                  {ch.params.map((p) => (
                    <div key={p.label_en}>
                      <dt className="text-[10px] uppercase tracking-[0.2em] text-[#5C544F]">{lang === "ar" ? p.label_ar : p.label_en}</dt>
                      <dd className="text-sm text-[#2A2421] mt-0.5">{p.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {ch.steps_en && (
                <ol className="space-y-3 pt-2">
                  {(lang === "ar" ? ch.steps_ar : ch.steps_en).map((s, i) => (
                    <li key={`${ch.id}-step-${i}`} className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-[#F9F6F0] border border-[#2A2421]/15 flex items-center justify-center text-xs font-display">
                        {i + 1}
                      </span>
                      <span className="text-[#2A2421] leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ol>
              )}

              {(ch.note_en || ch.kasani_note_en) && (
                <div className="rounded-xl bg-[#4A5D4E]/8 border border-[#4A5D4E]/20 p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5D4E] mb-1.5">
                    {isRTL ? "نصيحة الكاساني" : "Kasani's tip"}
                  </div>
                  <div className="text-sm text-[#2A2421] leading-relaxed">
                    {lang === "ar" ? (ch.note_ar || ch.kasani_note_ar) : (ch.note_en || ch.kasani_note_en)}
                  </div>
                </div>
              )}

              <AcademyComments chapterId={ch.id} trackId={track.id} />
            </div>
          </div>
        </article>

        {/* Prev / Next pager */}
        <div className="mt-12 pt-10 border-t border-[#2A2421]/10 grid sm:grid-cols-2 gap-4" data-testid="academy-pager">
          {chapterIdx > 0 ? (
            <button
              onClick={goPrev}
              className="text-left p-5 rounded-2xl border border-[#2A2421]/10 hover:bg-[#2A2421]/5 hover:border-[#4A5D4E]/30 transition-all"
              data-testid="academy-prev-chapter"
            >
              <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F] flex items-center gap-1">
                <PrevIcon className="w-3 h-3" /> {isRTL ? "السابق" : "Previous"}
              </div>
              <div className="text-[#2A2421] font-medium mt-2">
                {lang === "ar" ? chapters[chapterIdx - 1].title_ar : chapters[chapterIdx - 1].title_en}
              </div>
            </button>
          ) : <div />}
          {chapterIdx < chapters.length - 1 && (
            <button
              onClick={goNext}
              className="text-right p-5 rounded-2xl border border-[#2A2421]/10 hover:bg-[#2A2421]/5 hover:border-[#4A5D4E]/30 transition-all"
              data-testid="academy-next-chapter"
            >
              <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F] flex items-center gap-1 justify-end">
                {isRTL ? "التالي" : "Next"} <NextIcon className="w-3 h-3" />
              </div>
              <div className="text-[#2A2421] font-medium mt-2">
                {lang === "ar" ? chapters[chapterIdx + 1].title_ar : chapters[chapterIdx + 1].title_en}
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
